import React, { useEffect, useRef } from 'react'
import ReactECharts from 'echarts-for-react'
import { Card, Typography, Empty, Table, Tag, Row, Col } from 'antd'

const { Title, Text } = Typography

interface StatsChartsProps {
  data: any
  statType: string
}

const StatsCharts: React.FC<StatsChartsProps> = ({ data, statType }) => {
  const getChartOption = () => {
    if (!data) return null

    // 根据统计类型生成不同的图表
    if (statType === '按角色（起草、归口、发布、提出）标准数量统计') {
      const roleData = data.roles_distribution || []
      return {
        title: {
          text: '按角色标准数量统计',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: roleData.map((item: any) => item.机构角色),
          axisLabel: {
            interval: 0,
            rotate: 0
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [{
          data: roleData.map((item: any) => item.标准数量),
          type: 'bar',
          itemStyle: {
            color: '#1890ff'
          }
        }]
      }
    }

    if (statType === '机构参与标准的类型分布（按标准类别）') {
      const typeData = data.type_distribution?.filter((item: any) => item.参与数量 > 0) || []
      return {
        title: {
          text: '标准类型分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: typeData.map((item: any) => item.标准类别)
        },
        series: [{
          name: '标准类型',
          type: 'pie',
          radius: '50%',
          center: ['50%', '50%'],
          data: typeData.map((item: any) => ({
            value: item.参与数量,
            name: item.标准类别
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      }
    }

    if (statType === '机构标准产出时间趋势（按发布年份）') {
      const trendData = data.yearly_trend || []
      return {
        title: {
          text: '年度产出趋势',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis'
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: trendData.map((item: any) => item.发布年份),
          axisLabel: {
            interval: 0,
            rotate: 0
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [{
          data: trendData.map((item: any) => item.年度发布数量),
          type: 'line',
          smooth: true,
          itemStyle: {
            color: '#52c41a'
          }
        }]
      }
    }

    if (statType === '共同起草机构合作网络（前N合作机构）') {
      const partnerData = data.co_drafting_partners || []
      return {
        title: {
          text: '合作机构网络',
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: partnerData.map((item: any) => item.合作机构),
          axisLabel: {
            rotate: 45,
            interval: 0,
            formatter: function(value: string) {
              return value.length > 8 ? value.substring(0, 8) + '...' : value;
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: [{
          data: partnerData.map((item: any) => item.共同起草数量),
          type: 'bar',
          itemStyle: {
            color: '#722ed1'
          }
        }]
      }
    }

    if (statType === '机构起草标准领域分布(CCS分类占比)') {
      const classData = data.classification_distribution?.filter((item: any) => item.数量 > 0) || []
      return {
        title: {
          text: 'CCS分类分布',
          left: 'center'
        },
        tooltip: {
          trigger: 'item',
          formatter: '{a} <br/>{b}: {c} ({d}%)'
        },
        legend: {
          orient: 'vertical',
          left: 'left',
          data: classData.map((item: any) => item.分类号)
        },
        series: [{
          name: 'CCS分类',
          type: 'pie',
          radius: '50%',
          center: ['50%', '50%'],
          data: classData.map((item: any) => ({
            value: item.数量,
            name: item.分类号
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }]
      }
    }

    // 默认显示空状态
    return null
  }

  const getDetailTable = () => {
    if (!data) return null

    if (statType === '按角色（起草、归口、发布、提出）标准数量统计') {
      const roleData = data.roles_distribution || []
      return (
        <Table
          dataSource={roleData}
          columns={[
            {
              title: '机构角色',
              dataIndex: '机构角色',
              key: '机构角色',
              render: (text: string) => <Tag color="blue">{text}</Tag>
            },
            {
              title: '标准数量',
              dataIndex: '标准数量',
              key: '标准数量',
              render: (value: number) => <Text strong>{value}</Text>
            },
            {
              title: '标准详情',
              dataIndex: '标准列表',
              key: '标准列表',
              render: (list: any[]) => (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {list?.map((item: any, index: number) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {item.标准号} - {item.标准名称}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      )
    }

    if (statType === '机构参与标准的类型分布（按标准类别）') {
      const typeData = data.type_distribution?.filter((item: any) => item.参与数量 > 0) || []
      return (
        <Table
          dataSource={typeData}
          columns={[
            {
              title: '标准类别',
              dataIndex: '标准类别',
              key: '标准类别',
              render: (text: string) => <Tag color="green">{text}</Tag>
            },
            {
              title: '参与数量',
              dataIndex: '参与数量',
              key: '参与数量',
              render: (value: number) => <Text strong>{value}</Text>
            },
            {
              title: '标准详情',
              dataIndex: '标准列表',
              key: '标准列表',
              render: (list: any[]) => (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {list?.map((item: any, index: number) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {item.标准号} - {item.标准名称}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      )
    }

    if (statType === '机构标准产出时间趋势（按发布年份）') {
      const trendData = data.yearly_trend || []
      return (
        <Table
          dataSource={trendData}
          columns={[
            {
              title: '发布年份',
              dataIndex: '发布年份',
              key: '发布年份',
              render: (text: number) => <Tag color="orange">{text}</Tag>
            },
            {
              title: '年度发布数量',
              dataIndex: '年度发布数量',
              key: '年度发布数量',
              render: (value: number) => <Text strong>{value}</Text>
            },
            {
              title: '标准详情',
              dataIndex: '标准列表',
              key: '标准列表',
              render: (list: any[]) => (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {list?.map((item: any, index: number) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {item.标准号} - {item.标准名称}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      )
    }

    if (statType === '共同起草机构合作网络（前N合作机构）') {
      const partnerData = data.co_drafting_partners || []
      return (
        <Table
          dataSource={partnerData}
          columns={[
            {
              title: '合作机构',
              dataIndex: '合作机构',
              key: '合作机构',
              width:1,
              render: (text: string) => <Tag color="purple">{text}</Tag>
            },
            {
              title: '共同起草数量',
              dataIndex: '共同起草数量',
              key: '共同起草数量',
              width: 'auto',
              render: (value: number) => <Text strong>{value}</Text>
            },
            {
              title: '标准详情',
              dataIndex: '标准列表',
              key: '标准列表',
              width: 'auto',
              render: (list: any[]) => (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {list?.map((item: any, index: number) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {item.标准号} - {item.标准名称}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      )
    }

    if (statType === '机构起草标准领域分布(CCS分类占比)') {
      const classData = data.classification_distribution?.filter((item: any) => item.数量 > 0) || []
      return (
        <Table
          dataSource={classData}
          columns={[
            {
              title: '分类号',
              dataIndex: '分类号',
              key: '分类号',
              render: (text: string) => <Tag color="cyan">{text}</Tag>
            },
            {
              title: '分类名称',
              dataIndex: '分类名称',
              key: '分类名称',
              render: (text: string) => <Text>{text || '-'}</Text>
            },
            {
              title: '数量',
              dataIndex: '数量',
              key: '数量',
              render: (value: number) => <Text strong>{value}</Text>
            },
            {
              title: '标准详情',
              dataIndex: '标准列表',
              key: '标准列表',
              render: (list: any[]) => (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {list?.map((item: any, index: number) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {item.标准号} - {item.标准名称}
                    </div>
                  ))}
                </div>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      )
    }

    return null
  }

  const chartOption = getChartOption()
  const detailTable = getDetailTable()

  if (!chartOption && !detailTable) {
    return (
      <Card>
        <Empty 
          description="暂无图表数据"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    )
  }

  return (
    <Row gutter={[24, 24]}>
      {/* 图表显示 */}
      {chartOption && (
        <Col span={24}>
          <Card>
            <ReactECharts
              key={statType}
              option={chartOption}
              style={{ height: '400px', width: '100%' }}
              opts={{ renderer: 'canvas' }}
            />
          </Card>
        </Col>
      )}
      
      {/* 详细信息表格 */}
      {detailTable && (
        <Col span={24}>
          <Card title="详细信息">
            {detailTable}
          </Card>
        </Col>
      )}
    </Row>
  )
}

export default StatsCharts
